import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../config/api";
import ProductCard from "../components/ProductCard/ProductCard";
import {
  DEFAULT_JEWELRY_MENU_GROUPS,
  DEFAULT_JEWELRY_METALS,
  findJewelryMenuGroupById,
  findJewelryMenuGroupByMaterial,
} from "../utils/materialCatalog";
import { normalizeMaterialLabel } from "../utils/materialLabels";
import "../pages/ProductGrid.css";
import { useLocation, useNavigate } from "react-router-dom";
import { FaFilter, FaTimes } from "react-icons/fa";
import { bindStorefrontRefresh } from "../utils/storefrontRefresh";

/* const LEGACY_PRICE_RANGES = [
  { label: "All Prices", value: "" },
  { label: "Under ₹500", value: "0-500" },
  { label: "₹500 - ₹1000", value: "500-1000" },
  { label: "₹1000 - ₹2000", value: "1000-2000" },
  { label: "₹2000 - ₹5000", value: "2000-5000" },
  { label: "₹5000 - ₹10000", value: "5000-10000" },
  { label: "₹10000 and above", value: "10000+" },
]; */

const PRICE_RANGES = [
  { label: "All Prices", value: "" },
  { label: "Up to 499", value: "0-499" },
  { label: "500-999", value: "500-999" },
  { label: "1000-2999", value: "1000-2999" },
  { label: "3000-4999", value: "3000-4999" },
  { label: "5000-9999", value: "5000-9999" },
  { label: "10000-14999", value: "10000-14999" },
  { label: "15000-19999", value: "15000-19999" },
  { label: "20000-24999", value: "20000-24999" },
  { label: "25000 and above", value: "25000+" },
];

const GEMSTONE_TYPES = ["PRECIOUS", "SEMI-PRECIOUS", "LAB GROWN (LGD)"];

const GEMSTONE_SHAPES = [
  "Rounds",
  "Emerald cut",
  "Princess cut", 
  "Cushion cut",
  "Heart shape",
  "Marquise",
  "Pear",
  "Trillion",
  "Oval"
];
const GEMSTONE_OPTIONS = {
  "PRECIOUS": [
    "Natural Diamonds",
    "Ruby",
    "Emerald",
    "Coral",
    "Yellow Sapphire",
    "Blue Sapphire",
    "Pearls",
    "Hessonite (Gomed)",
    "Catseye"
  ],
  "SEMI-PRECIOUS": [
    "Amethyst",
    "Peridot",
    "Topaz",
    "Turquoise/Firoza",
    "Malachite",
    "Lapis lazuli",
    "Crystal/Sphatik",
    "Opal",
    "Citrine/Sunela",
    "Pink Tourmaline",
    "Golden pyrite",
    "Rose Quartz",
    "Tiger Eye",
    "Yellow Amber"
  ],
  "LAB GROWN (LGD)": [
    "Diamonds",
    "Ruby",
    "Emerald",
    "Yellow Sapphire",
    "Blue Sapphire"
  ]
};

const SPIRITUAL_ITEMS = [
  "RUDRAKSH",
  "BLOWING COANCH (SHANKH)",
  "RIGHT HAND COANCH (DAKSHINAVARTI)",
  "ONE EYE COCONUT (EKAKSHI SHREEFAL)",
];

const CATEGORY_LABELS = {
  PRECIOUS: "Precious",
  "SEMI-PRECIOUS": "Semi-Precious",
  "LAB GROWN (LGD)": "Lab Grown (LGD)",
  RUDRAKSH: "Rudraksh",
  "BLOWING COANCH (SHANKH)": "Blowing Coanch (Shankh)",
  "RIGHT HAND COANCH (DAKSHINAVARTI)": "Right Hand Coanch (Dakshinavarti)",
  "ONE EYE COCONUT (EKAKSHI SHREEFAL)": "One Eye Coconut (Ekakshi Shreefal)",
};

const formatCategoryLabel = (value = "") =>
  CATEGORY_LABELS[value] || normalizeMaterialLabel(value) || value;

const DEFAULT_STORE_SORT = "newest";
const STORE_CATEGORY_PREVIEW_COUNT = 5;
const STORE_CATEGORY_CONFIG = [
  {
    id: "brass-cz-colour-stone-imitation",
    heading: "Metal : Brass",
    subtitle: "",
  },
  {
    id: "stainless-fashion",
    heading: "Metal : Steel",
    subtitle: "",
  },
  {
    id: "silver",
    heading: "Metal : Silver",
    subtitle: "",
  },
  {
    id: "gold",
    heading: "Metal : Gold",
    subtitle: "",
  },
  {
    id: "platinum",
    heading: "Metal : Platinum",
    subtitle: "",
  },
  {
    id: "platinum-gold",
    heading: "Metal : Platinum-Gold",
    subtitle: "",
  },
];
const STORE_CATEGORY_GROUPS = STORE_CATEGORY_CONFIG
  .map((group) => ({
    ...group,
    ...findJewelryMenuGroupById(group.id, DEFAULT_JEWELRY_MENU_GROUPS),
  }))
  .filter(Boolean);
const buildEmptyFeaturedByGroup = () =>
  STORE_CATEGORY_GROUPS.reduce((accumulator, group) => {
    accumulator[group.id] = [];
    return accumulator;
  }, {});

const normalizeFeaturedByGroup = (value = {}) =>
  STORE_CATEGORY_GROUPS.reduce((accumulator, group) => {
    accumulator[group.id] = [...new Set(
      (Array.isArray(value?.[group.id]) ? value[group.id] : [])
        .map((productId) => String(productId || "").trim())
        .filter(Boolean)
    )].slice(0, STORE_CATEGORY_PREVIEW_COUNT);
    return accumulator;
  }, buildEmptyFeaturedByGroup());

const getStoreCategoryMeta = (group) => ({
  heading: group?.heading || group?.label || "",
  subtitle: group?.subtitle || "",
});

const buildFeaturedPreviewProducts = (groupedProducts = [], featuredIds = []) => {
  const productLookup = new Map(
    groupedProducts.map((product) => [String(product?._id || ""), product])
  );
  const featuredProducts = (Array.isArray(featuredIds) ? featuredIds : [])
    .map((productId) => productLookup.get(String(productId || "")))
    .filter(Boolean);
  const seenProductIds = new Set(
    featuredProducts.map((product) => String(product?._id || ""))
  );

  return [
    ...featuredProducts,
    ...groupedProducts.filter(
      (product) => !seenProductIds.has(String(product?._id || ""))
    ),
  ].slice(0, STORE_CATEGORY_PREVIEW_COUNT);
};

const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [selectedGemstone, setSelectedGemstone] = useState("");
  const [selectedGemstoneShape, setSelectedGemstoneShape] = useState("");
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [materials, setMaterials] = useState(DEFAULT_JEWELRY_METALS);
  const [featuredByGroup, setFeaturedByGroup] = useState(buildEmptyFeaturedByGroup);
  const location = useLocation();
  const navigate = useNavigate();

  // Parse filters from query string
  const getFiltersFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      material: searchParams.get("material") || "",
      product: searchParams.get("product") || "",
      recipient: searchParams.get("recipient") || "",
      price: searchParams.get("price") || "",
      search: searchParams.get("search") || "",
      gemstone: searchParams.get("gemstone") || "",
      gemstoneShape: searchParams.get("gemstoneShape") || "",
      storeCategory: searchParams.get("storeCategory") || "",
    };
  }, [location.search]);

  // Update URL query for price
  const updateQuery = (priceValue) => {
    const searchParams = new URLSearchParams(location.search);
    if (priceValue) {
      searchParams.set('price', priceValue);
    } else {
      searchParams.delete('price');
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  // Add a handler for material selection
  const handleMaterialChange = (e) => {
    const value = e.target.value;
    setSelectedMaterial(value);
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set('material', value);
    } else {
      searchParams.delete('material');
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  // Update URL query for specific gemstone
  const handleGemstoneChange = (e) => {
    const value = e.target.value;
    setSelectedGemstone(value);
    setSelectedGemstoneShape(""); // Reset shape when gemstone changes
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set("gemstone", value);
    } else {
      searchParams.delete("gemstone");
    }
    searchParams.delete("gemstoneShape"); // Remove shape filter when gemstone changes
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  // Update URL query for gemstone shape
  const handleGemstoneShapeChange = (e) => {
    const value = e.target.value;
    setSelectedGemstoneShape(value);
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set("gemstoneShape", value);
    } else {
      searchParams.delete("gemstoneShape");
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const fetchMaterials = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/navbar-dropdown`);

      if (Array.isArray(response.data?.jewelryMetals) && response.data.jewelryMetals.length) {
        setMaterials(response.data.jewelryMetals);
      }
    } catch (error) {
      console.error("Failed to load jewelry metals:", error);
    }
  }, []);

  const fetchStoreFeaturedProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/store-featured-products`);
      setFeaturedByGroup(
        normalizeFeaturedByGroup(response.data?.featuredByGroup || {})
      );
    } catch (error) {
      console.error("Failed to load store featured products:", error);
      setFeaturedByGroup(buildEmptyFeaturedByGroup());
    }
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const { material, product, recipient, price, search, gemstone, gemstoneShape } = getFiltersFromQuery();
      const queryParams = { 
        sort: DEFAULT_STORE_SORT,
        limit: 'all' // Request all products instead of default 10
      };
      if (GEMSTONE_TYPES.includes(material)) {
        queryParams.material = material;
        if (gemstone) queryParams.gemstone = gemstone;
        if (gemstoneShape) queryParams.gemstoneShape = gemstoneShape;
      } else if (material) {
        queryParams.material = material;
      }
      if (product) queryParams.product = product;
      if (recipient) queryParams.recipient = recipient;
      if (price) queryParams.price = price;
      if (search) queryParams.search = search;
      const res = await axios.get(`${API_BASE}/products`, {
        params: queryParams,
      });
      setProducts(res.data);
    } catch (err) {
      console.error("Failed to load products:", err);
    }
    setIsLoading(false);
  }, [getFiltersFromQuery]);

  // Fetch when sort/filter changes
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  useEffect(() => {
    fetchStoreFeaturedProducts();
  }, [fetchStoreFeaturedProducts]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => bindStorefrontRefresh(fetchProducts, { scopes: ["products"] }), [fetchProducts]);
  useEffect(() => bindStorefrontRefresh(fetchMaterials, { scopes: ["navigation"] }), [fetchMaterials]);
  useEffect(() => bindStorefrontRefresh(fetchStoreFeaturedProducts, { scopes: ["products"] }), [fetchStoreFeaturedProducts]);

  // On mount and when location changes, set state from query
  useEffect(() => {
    const { price, material, gemstone, gemstoneShape } = getFiltersFromQuery();
    setSelectedPrice(price || "");
    setSelectedMaterial(material || "");
    setSelectedGemstone(gemstone || "");
    setSelectedGemstoneShape(gemstoneShape || "");
  }, [getFiltersFromQuery]);

  // Handle price bucket change
  const handlePriceChange = (e) => {
    const value = e.target.value;
    setSelectedPrice(value);
    updateQuery(value);
  };

  // Close mobile filter after applying filter
  const handleMobileFilterChange = (handler, value) => {
    handler(value);
    // Close mobile filter after a short delay to show the change
    setTimeout(() => {
      setIsMobileFilterOpen(false);
    }, 500);
  };

  const handleMobilePriceChange = (e) => {
    const value = e.target.value;
    setSelectedPrice(value);
    updateQuery(value);
  };

  const clearAllFilters = () => {
    setSelectedMaterial("");
    setSelectedPrice("");
    setSelectedGemstone("");
    setSelectedGemstoneShape("");
    navigate({ search: "" }, { replace: true });
  };

  const categoryOptions = Array.from(
    new Set([
      ...materials.filter(Boolean),
      ...GEMSTONE_TYPES,
      ...SPIRITUAL_ITEMS,
    ])
  );

  const showGemstoneDropdown = GEMSTONE_TYPES.includes(selectedMaterial);
  const currentFilters = getFiltersFromQuery();
  const activeStoreCategory = findJewelryMenuGroupById(
    currentFilters.storeCategory,
    STORE_CATEGORY_GROUPS
  );
  const hasCatalogScopedFilters = Boolean(
    currentFilters.material
    || currentFilters.product
    || currentFilters.recipient
    || currentFilters.search
    || currentFilters.gemstone
    || currentFilters.gemstoneShape
  );

  const storeCategoryProducts = STORE_CATEGORY_GROUPS.reduce((groupsMap, group) => {
    groupsMap.set(group.id, []);
    return groupsMap;
  }, new Map());

  products.forEach((product) => {
    const matchedGroup = findJewelryMenuGroupByMaterial(
      normalizeMaterialLabel(product?.material || ""),
      STORE_CATEGORY_GROUPS
    );

    if (!matchedGroup || !storeCategoryProducts.has(matchedGroup.id)) {
      return;
    }

    storeCategoryProducts.get(matchedGroup.id).push(product);
  });

  const storeCategorySections = STORE_CATEGORY_GROUPS
    .map((group) => {
      const groupedProducts = storeCategoryProducts.get(group.id) || [];

      if (!groupedProducts.length) {
        return null;
      }

      return {
        id: group.id,
        heading: getStoreCategoryMeta(group).heading,
        subtitle: getStoreCategoryMeta(group).subtitle,
        products: groupedProducts,
        previewProducts: buildFeaturedPreviewProducts(
          groupedProducts,
          featuredByGroup[group.id]
        ),
        hasMore: groupedProducts.length > STORE_CATEGORY_PREVIEW_COUNT,
      };
    })
    .filter(Boolean);

  const activeStoreCategorySection = activeStoreCategory
    ? {
        id: activeStoreCategory.id,
        heading: getStoreCategoryMeta(activeStoreCategory).heading,
        subtitle: getStoreCategoryMeta(activeStoreCategory).subtitle,
        products: storeCategoryProducts.get(activeStoreCategory.id) || [],
      }
    : null;

  const isSectionedStoreView = !activeStoreCategory && !hasCatalogScopedFilters && storeCategorySections.length > 0;
  const isSingleStoreCategoryView = Boolean(activeStoreCategorySection);
  const visibleProducts = isSingleStoreCategoryView
    ? activeStoreCategorySection.products
    : products;

  const openStoreCategoryView = (categoryId) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("storeCategory", categoryId);
    navigate({ search: searchParams.toString() });
  };

  const closeStoreCategoryView = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("storeCategory");
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const renderPriceFilterSelect = () => (
    <div className="store-toolbar-controls">
      <label className="store-toolbar-select">
        <select value={selectedPrice} onChange={handlePriceChange}>
          {PRICE_RANGES.map((range) => (
            <option key={range.value} value={range.value}>{range.label}</option>
          ))}
        </select>
      </label>
    </div>
  );

  return (
    <div className="product-page-container">
      {/* Desktop Filters */}
      {!isSectionedStoreView ? (
        <div className="desktop-filters">
          <div className="store-toolbar-shell">
            {renderPriceFilterSelect()}
          </div>

          {showGemstoneDropdown ? (
            <div className="store-toolbar-secondary">
              <label className="store-toolbar-select">
                <select value={selectedGemstone} onChange={handleGemstoneChange}>
                  <option value="">Choose Your Gemstone</option>
                  {GEMSTONE_OPTIONS[selectedMaterial].map((gem) => (
                    <option key={gem} value={gem}>{gem}</option>
                  ))}
                </select>
              </label>

              {selectedGemstone ? (
                <label className="store-toolbar-select">
                  <select value={selectedGemstoneShape} onChange={handleGemstoneShapeChange}>
                    <option value="">Choose Shape</option>
                    {GEMSTONE_SHAPES.map((shape) => (
                      <option key={shape} value={shape}>{shape}</option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Mobile Filter Toggle */}
      <div className="mobile-filter-toggle">
        <button 
          className="filter-toggle-btn"
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
        >
          <FaFilter />
          <span>Filters</span>
        </button>
      </div>

      {/* Mobile Filter Sidebar */}
      <div className={`mobile-filter-sidebar ${isMobileFilterOpen ? 'open' : ''}`}>
        <div className="mobile-filter-header">
          <h3>Filters</h3>
          <button 
            className="close-filter-btn"
            onClick={() => setIsMobileFilterOpen(false)}
          >
            <FaTimes />
          </button>
        </div>
        
        <div className="mobile-filter-content">
          <div className="mobile-filter-section">
            <label>Category</label>
            <select
              value={selectedMaterial}
              onChange={handleMaterialChange}
              className="mobile-filter-select"
            >
              <option value="">All Categories</option>
              {categoryOptions.map((option) => (
                <option key={option} value={option}>{formatCategoryLabel(option)}</option>
              ))}
            </select>
          </div>

          {showGemstoneDropdown ? (
            <div className="mobile-filter-section">
              <label>Choose Your Gemstone</label>
              <select
                value={selectedGemstone}
                onChange={handleGemstoneChange}
                className="mobile-filter-select"
              >
                <option value="">Choose Your Gemstone</option>
                {GEMSTONE_OPTIONS[selectedMaterial].map((gem) => (
                  <option key={gem} value={gem}>{gem}</option>
                ))}
              </select>
            </div>
          ) : null}

          {showGemstoneDropdown && selectedGemstone && (
            <div className="mobile-filter-section">
              <label>Gemstone Shape</label>
              <select 
                value={selectedGemstoneShape} 
                onChange={(e) => handleMobileFilterChange(handleGemstoneShapeChange, e)}
                className="mobile-filter-select"
              >
                <option value="">Choose Shape</option>
                {GEMSTONE_SHAPES.map((shape) => (
                  <option key={shape} value={shape}>{shape}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mobile-filter-section">
            <label>Price Range</label>
            <select 
              value={selectedPrice} 
              onChange={handleMobilePriceChange}
              className="mobile-filter-select"
            >
              {PRICE_RANGES.map((range) => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>

          <div className="mobile-filter-actions">
            <button 
              className="apply-filters-btn"
              onClick={() => setIsMobileFilterOpen(false)}
            >
              Apply Filters
            </button>
            <button 
              className="clear-filters-btn"
              onClick={() => {
                clearAllFilters();
                setIsMobileFilterOpen(false);
              }}
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Filter Overlay */}
      {isMobileFilterOpen && (
        <div 
          className="mobile-filter-overlay"
          onClick={() => setIsMobileFilterOpen(false)}
        />
      )}

      {/* Product grid and empty state */}
      {!isLoading && !isSectionedStoreView && visibleProducts.length === 0 ? (
        <div className={isSingleStoreCategoryView ? "store-category-detail" : ""}>
          {isSingleStoreCategoryView ? (
            <div className="store-category-detail-header">
              <div className="store-category-heading-copy">
                <h2 className="store-category-title">
                  {activeStoreCategorySection.heading}
                </h2>
              </div>
              <button
                type="button"
                className="no-products-button store-category-nav-button"
                onClick={closeStoreCategoryView}
              >
                Back
              </button>
            </div>
          ) : null}

          <div className="no-products-message">
            <h3>No products found in this category</h3>
            <p>
              We are adding new designs soon. Please try a different category or clear the filters to see all products.
            </p>
            <button
              className="no-products-button"
              onClick={() => navigate("/store", { replace: true })}
            >
              Browse all products
            </button>
          </div>
        </div>
      ) : (
        <>
          {isSingleStoreCategoryView ? (
            <div className="store-category-detail">
              <div className="store-category-detail-header">
                <div className="store-category-heading-copy">
                  <h2 className="store-category-title">
                    {activeStoreCategorySection.heading}
                  </h2>
                </div>
                <button
                  type="button"
                  className="no-products-button store-category-nav-button"
                  onClick={closeStoreCategoryView}
                >
                  Back
                </button>
              </div>

              <div className="product-grid">
                {visibleProducts.map((product) => (
                  <ProductCard product={product} key={product._id} />
                ))}
              </div>
            </div>
          ) : isSectionedStoreView ? (
            <div className="store-category-sections">
              {storeCategorySections.map((section, sectionIndex) => {
                const previousSection = sectionIndex > 0 ? storeCategorySections[sectionIndex - 1] : null;
                const isLastSection = sectionIndex === storeCategorySections.length - 1;

                return (
                <section className="store-category-section" key={section.id}>
                  <div className="store-category-section-header store-category-section-header--toolbar">
                    <div className="store-category-heading-copy">
                      <h2 className="store-category-title">{section.heading}</h2>
                    </div>
                    {sectionIndex === 0 ? (
                      <div className="store-category-header-price-filter">
                        {renderPriceFilterSelect()}
                      </div>
                    ) : previousSection?.hasMore ? (
                      <button
                        type="button"
                        className="no-products-button store-category-nav-button"
                        onClick={() => openStoreCategoryView(previousSection.id)}
                      >
                        More
                      </button>
                    ) : null}
                  </div>

                  <div className="product-grid">
                    {section.previewProducts.map((product) => (
                      <ProductCard product={product} key={product._id} />
                    ))}
                  </div>

                  {isLastSection && section.hasMore ? (
                    <div className="store-category-section-footer">
                      <button
                        type="button"
                        className="no-products-button store-category-nav-button"
                        onClick={() => openStoreCategoryView(section.id)}
                      >
                        More
                      </button>
                    </div>
                  ) : null}
                </section>
                );
              })}
            </div>
          ) : (
            <div className="product-grid">
              {visibleProducts.map((product) => (
                <ProductCard product={product} key={product._id} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductGrid;
